/**
 * SymptomCheckModal v120.4 — Chat Dialogue Symptom Check-In
 * 
 * Gated question progression:
 *   - Orella greeting bubble
 *   - Each question appears as SmallBubble → card
 *   - Negative answers auto-advance
 *   - Non-negative answers show detail textbox before advancing
 *   - Progress bar tracks completion
 */

import { useState, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { ArrowLeft, Check, X } from 'lucide-react'
import { SmallOrellaBubble, AnimatedCard, ChatKeyframes } from '../../components/OrellaChatHeader'
import orellaOIcon from '../../assets/logos/orella_o_icon.png'
import { useI18n } from '../../i18n'
import { DIALOGUE_BUBBLE, DIALOGUE_PAD } from '../../components/agent/dialogueBubble'

const NAVY = '#15354F'
const BLUE = '#46A8F9'
const ICE_BLUE = '#E9F8FF'
const GREEN = '#66D170'
const GREEN_DARK = '#16A34A'

function InlineBubble({ message, visible, delay = 0, onComplete }) {
  const { t } = useI18n()
  const [showLogo, setShowLogo] = useState(false)
  const [showBubble, setShowBubble] = useState(false)
  const [showDots, setShowDots] = useState(false)
  const [showText, setShowText] = useState(false)
  const calledRef = useRef(false)

  useEffect(() => {
    if (!visible) return
    const t = []
    t.push(setTimeout(() => setShowLogo(true), delay + 200))
    t.push(setTimeout(() => { setShowBubble(true); setShowDots(true) }, delay + 400))
    t.push(setTimeout(() => { 
      setShowDots(false); setShowText(true)
      if (!calledRef.current) { calledRef.current = true; onComplete?.() }
    }, delay + 1200))
    return () => t.forEach(clearTimeout)
  }, [visible, delay])

  if (!visible) return null

  return (
    <div className="px-4 pt-3 pb-1">
      <div className="flex items-start gap-2.5">
        <img src={orellaOIcon} alt="O"
          className="w-9 h-9 rounded-[10px] flex-shrink-0 object-contain transition-all duration-[400ms]"
          style={{ opacity: showLogo ? 1 : 0, transform: showLogo ? 'scale(1)' : 'scale(0.75)' }} />
        <div className={`flex-1 ${DIALOGUE_BUBBLE} ${DIALOGUE_PAD} transition-all duration-[400ms]`}
          style={{ opacity: showBubble ? 1 : 0, transform: showBubble ? 'translateY(0) scale(1)' : 'translateY(4px) scale(0.95)' }}>
          {showDots && !showText && (
            <span className="inline-flex items-center gap-1 h-5">
              {[0,1,2].map(i => <span key={i} className="w-1.5 h-1.5 rounded-full bg-[#A7D9FC] animate-[dotPulse_1.2s_ease-in-out_infinite]" style={{ animationDelay: `${i*0.2}s` }} />)}
            </span>
          )}
          {showText && <p className="text-[15.5px] font-medium text-[#15354F] animate-[fadeIn_0.3s_ease]" style={{ lineHeight: 1.55 }}>{message}</p>}
        </div>
      </div>
    </div>
  )
}

export default function SymptomCheckModal({ open, onClose, onSubmit, questions, caseLabel }) {
  const { t } = useI18n()
  const [step, setStep] = useState(0)
  const [answers, setAnswers] = useState({})
  const [details, setDetails] = useState({})
  const scrollRef = useRef(null)

  useEffect(() => {
    if (!open) { setStep(0); setAnswers({}); setDetails({}) }
  }, [open])

  // Auto-scroll to bottom when step changes
  useEffect(() => {
    if (scrollRef.current) {
      setTimeout(() => scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' }), 600)
    }
  }, [step])

  if (!open || !questions?.length) return null

  const answeredCount = Object.keys(answers).length
  const allDone = answeredCount === questions.length

  const handleAnswer = (qKey, optIndex, isNegative) => {
    setAnswers(a => ({ ...a, [qKey]: optIndex }))
    if (isNegative) {
      // Auto-advance past this question
      const qIdx = questions.findIndex(q => q.key === qKey)
      setTimeout(() => setStep(s => Math.max(s, qIdx + 2)), 400)
    }
  }

  const advancePast = (qKey) => {
    const qIdx = questions.findIndex(q => q.key === qKey)
    setStep(s => Math.max(s, qIdx + 2))
  }

  // ★★★ D332(f) — THE SIGNAL LEAVES WITH THE ANSWER, NOT INSTEAD OF IT.
  //   This function used to emit `q.options[index]` and nothing else, so the two facts that
  //   actually carry meaning — WHICH option was chosen, and whether the question's own
  //   author marked it reassuring — died here. Everything downstream was then forced to
  //   compare LOCALISED DISPLAY PROSE: `DeliverScreen` matched against `t('deliver.
  //   yesNewSymptoms')`, which only works because both sides are produced by the same
  //   `t()` in the same session, and the SERVER could not compare anything at all because
  //   it receives text in one of eleven languages.
  //   `structured` is additive: every existing reader of `result[q.key]` is untouched.
  const handleSubmit = () => {
    const result = {}
    const structured = {}
    questions.forEach(q => {
      if (answers[q.key] === undefined) return
      if (q.type === 'free-text') {
        result[q.key] = details[q.key] || ''
        return
      }
      const index = answers[q.key]
      const negativeIndices = q.negativeIndices || q.neg || [0, 1]
      result[q.key] = q.options[index]
      if (details[q.key]) result[`${q.key}_detail`] = details[q.key]
      structured[q.key] = {
        index,
        count: (q.options || []).length,
        // `reassuring` is this codebase's "negative finding" — the answer the question's
        // author marked as nothing-to-act-on. Named for what it means, not for its sign.
        reassuring: negativeIndices.includes(index),
        option: q.options[index],
      }
    })
    result.structured = structured
    onSubmit(result)
  }

  // Intro messages per question index
  // ★★★ v242.153.0 · CHECKUP-COPY-1 — ONE GREETING, NOT TWO. The opening bubble
  //   said "Let's check in on your symptoms…" and the first question's bubble then
  //   said "Let's start with your primary symptom." — two Orella bubbles stacked
  //   before the patient had answered anything, both saying we are beginning.
  //   `letsCheckIn` now carries the whole greeting INCLUDING the hand-off to the
  //   first symptom, and question 0 gets no bubble of its own.
  // ★★ AND BOTH OF THOSE STRINGS WERE HARDCODED ENGLISH inside a screen that is
  //   otherwise fully translated — a Spanish patient read the greeting in Spanish
  //   and the next line in English. `lastQuestion` is a key now for the same reason.
  const getIntro = (qi) => {
    if (qi === 0) return ''
    if (qi === questions.length - 1) return t('symptomCheck.lastQuestion')
    return t('symptomCheck.questionProgress', { current: qi + 1, total: questions.length })
  }

  return createPortal(
    <div className="fixed inset-0 z-50 flex flex-col bg-[#F8FAFC]" style={{ animation: 'fadeSlideUp 0.35s cubic-bezier(0.16,1,0.3,1)' }}>
      <ChatKeyframes />
      <style>{`@keyframes fadeSlideUp { from { opacity:0; transform:translateY(16px); } to { opacity:1; transform:translateY(0); } }`}</style>

      {/* Header */}
      {/* ★★ v242.87.0 (TF b35 #28) — "Overlap in menu". This header had py-3 and
          no safe-area top inset, so on a Dynamic Island device the title sat
          under the clock. It is a createPortal, so MobileLayout's inset never
          reaches it and nothing upstream can compensate — a portaled full-screen
          header must paint its own. The expression below is the app's canonical
          one, byte-identical to CheckInModal.jsx, ViewSummary.jsx and
          CaseHome.jsx. Gated by scripts/check-safe-area.mjs. */}
      <div className="flex items-center gap-2.5 px-4 pb-3 border-b border-gray-100 bg-white flex-shrink-0"
           data-safe-top="1"
           style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 12px)' }}>
        <button onClick={onClose} className="p-1"><ArrowLeft size={22} style={{ color: NAVY }} /></button>
        <span className="text-[15px] font-bold" style={{ color: NAVY }}>{t('symptomCheck.symptomCheckin')}</span>
        <button onClick={onClose} className="p-1 ml-auto"><X size={22} className="text-gray-400" /></button>
      </div>

      {/* Progress */}
      <div className="px-4 py-2.5 bg-white border-b border-gray-100">
        <div className="flex justify-between mb-1">
          <span className="text-[11px] font-semibold text-gray-500">{caseLabel || t('symptomCheck.symptomCheckin')}</span>
          <span className="text-[11px] font-semibold" style={{ color: GREEN_DARK }}>{answeredCount}/{questions.length}</span>
        </div>
        <div className="h-1 bg-gray-100 rounded-full overflow-hidden">
          <div className="h-full rounded-full transition-all duration-500" style={{ width: `${(answeredCount / questions.length) * 100}%`, backgroundColor: GREEN }} />
        </div>
      </div>

      {/* Scrollable Content */}
      <div ref={scrollRef} className="flex-1 overflow-auto pb-4">
        <InlineBubble visible={true} delay={0} onComplete={() => setStep(1)}
          message={t('symptomCheck.letsCheckIn')} />

        {questions.map((q, qi) => {
          const qStep = qi + 1
          const isActive = step >= qStep
          const answered = answers[q.key] !== undefined
          const isFreeText = q.type === 'free-text'
          const negativeIndices = q.negativeIndices || q.neg || [0, 1]
          const isNegative = !isFreeText && answered && negativeIndices.includes(answers[q.key])
          const needsDetail = !isFreeText && answered && !isNegative

          if (!isActive) return null

          return (
            <div key={q.key}>
              {/* ★ no bubble for question 0 — the opening greeting already handed off to it. */}
              {getIntro(qi) ? <SmallOrellaBubble visible={true} delay={qStep === step ? 200 : 0} message={getIntro(qi)} /> : null}
              <AnimatedCard visible={true} delay={qStep === step ? 600 : 0}>
                <div className={`rounded-[14px] border-2 p-4 mb-0.5 ${answered ? 'border-green-200' : 'border-gray-200'} bg-white`}>
                  <p className="text-[13px] font-bold text-gray-800 mb-3">{q.question}</p>

                  {isFreeText ? (
                    /* v163.6.0: Free-text question (AI bridge) */
                    <div>
                      <textarea
                        value={details[q.key] || ''} onChange={e => {
                          setDetails(d => ({ ...d, [q.key]: e.target.value }))
                          if (!answered && e.target.value.length > 0) setAnswers(a => ({ ...a, [q.key]: 'free-text' }))
                          if (e.target.value.length === 0) setAnswers(a => { const n = { ...a }; delete n[q.key]; return n })
                        }}
                        placeholder={t('symptomCheck.typeResponseHere')}
                        className="w-full h-20 border-[1.5px] border-gray-200 rounded-[10px] p-2.5 text-[12px] text-gray-700 resize-none focus:border-blue-300 focus:outline-none" />
                      {answered && (
                        <button onClick={() => advancePast(q.key)}
                          className="mt-1.5 text-[11px] font-semibold border-none bg-transparent cursor-pointer" style={{ color: BLUE }}>
                          Continue →
                        </button>
                      )}
                    </div>
                  ) : (
                    /* Standard single/multi-select */
                    <>
                      <div className="flex flex-wrap gap-2">
                        {q.options.map((opt, oi) => (
                          <button key={oi}
                            onClick={() => handleAnswer(q.key, oi, negativeIndices.includes(oi))}
                            className="px-3.5 py-2 rounded-xl text-[12px] font-semibold border-2 transition-all"
                            style={{
                              borderColor: answers[q.key] === oi ? BLUE : '#E2E8F0',
                              background: answers[q.key] === oi ? ICE_BLUE : 'white',
                              color: answers[q.key] === oi ? NAVY : '#64748B',
                            }}>
                            {opt}
                          </button>
                        ))}
                      </div>

                      {needsDetail && (
                        <div className="mt-3 animate-[fadeIn_0.3s_ease]">
                          <p className="text-[11px] font-semibold text-gray-500 mb-1.5">{t('symptomCheck.tellUsMoreOptional')}</p>
                          <textarea
                            value={details[q.key] || ''} onChange={e => setDetails(d => ({ ...d, [q.key]: e.target.value }))}
                            placeholder={t('symptomCheck.describeExperiencing')}
                            className="w-full h-16 border-[1.5px] border-gray-200 rounded-[10px] p-2.5 text-[12px] text-gray-700 resize-none focus:border-blue-300 focus:outline-none" />
                          <button onClick={() => advancePast(q.key)}
                            className="mt-1 text-[11px] font-semibold border-none bg-transparent cursor-pointer" style={{ color: BLUE }}>
                            Continue →
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </AnimatedCard>
            </div>
          )
        })}

        {allDone && (
          <SmallOrellaBubble visible={true} delay={300} message={t('symptomCheck.allDoneResponses')} />
        )}
      </div>

      {/* Bottom Button */}
      <div className="p-4 pb-6 border-t border-gray-100 bg-white flex-shrink-0">
        <button onClick={handleSubmit} disabled={!allDone}
          className="w-full py-3.5 rounded-[14px] text-[14px] font-bold flex items-center justify-center gap-2 transition-all"
          style={{
            background: allDone ? `linear-gradient(135deg, ${NAVY}, #1E4A6E)` : '#E2E8F0',
            color: allDone ? '#fff' : '#94A3B8',
          }}>
          <Check size={16} /> {t('medReview.saveResponses', 'Save Responses')}
        </button>
      </div>
    </div>,
    document.body
  )
}
